'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { ArrowUpRight, ArrowRight } from 'lucide-react';

export interface Article {
  id: string;
  slug?: string;
  title: string;
  excerpt?: string;
  image?: string;
  category?: string;
  date?: string;
  readTime?: string;
  author?: string;
  isFeatured?: boolean;
}

export interface StoneInvestmentJournalSectionProps {
  articles: Article[];
  onArticleSelect?: (id: string) => void;
  /** Base path for article links, e.g. "/en/news". When provided, articles render as crawlable <a> elements. */
  articleBasePath?: string;
  messages?: Record<string, unknown>;
  locale?: string;
}

type NewsCategory = 'ALL' | 'MARKET' | 'DESIGN' | 'LIFESTYLE' | 'AGENCY';

function formatDate(dateString?: string, locale = 'en'): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const localeMap: Record<string, string> = { en: 'en-GB', fr: 'fr-FR', es: 'es-ES' };
    return new Intl.DateTimeFormat(localeMap[locale] || locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date).toUpperCase();
  } catch {
    return dateString;
  }
}

/**
 * Stone Investment - Journal Section
 * 
 * Concept One Design (Exact Match):
 * - Header with "The Journal" and category filters
 * - Featured article with floating card overlay
 * - Grid layout with different styles per category
 * - MARKET articles get horizontal layout
 * - Standard articles get vertical/editorial layout
 */
export function StoneInvestmentJournalSection({ 
  articles,
  onArticleSelect,
  articleBasePath,
  messages,
  locale = 'en'
}: StoneInvestmentJournalSectionProps) {
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const journalMessages = (stoneMessages.journal || {}) as Record<string, string>;

  const t = (key: string, fallback: string): string => {
    return journalMessages[key] || fallback;
  };

  const categoryLabels: Record<NewsCategory, string> = {
    ALL: t('categoryAll', 'ALL'),
    MARKET: t('categoryMarket', 'MARKET'),
    DESIGN: t('categoryDesign', 'DESIGN'),
    LIFESTYLE: t('categoryLifestyle', 'LIFESTYLE'),
    AGENCY: t('categoryAgency', 'AGENCY'),
  };

  const [activeCategory, setActiveCategory] = useState<NewsCategory>('ALL');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const featuredArticle = articles.find(a => a.isFeatured);
  const remainingArticles = articles.filter(a => !a.isFeatured);

  const filteredArticles = activeCategory === 'ALL' 
    ? remainingArticles 
    : remainingArticles.filter(a => a.category?.toUpperCase() === activeCategory);

  const basePath = articleBasePath || `/${locale}/news`;

  const getArticleHref = (article: Article): string => {
    return `${basePath}/${article.slug || article.id}`;
  };

  const handleSelect = (id: string) => {
    if (onArticleSelect) onArticleSelect(id);
  };

  return (
    <div className="w-full bg-stone-50 min-h-screen pt-32 pb-32">
      
      {/* --- HEADER --- */}
      <div className="container mx-auto px-6 md:px-12 mb-20 md:mb-32">
        <div className="flex flex-col md:flex-row justify-between items-end border-b border-stone-200 pb-12">
          <div className={`transition-all duration-1000 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-8 bg-stone-400" />
              <p className="font-sans text-xs uppercase tracking-[0.4em] text-stone-500">{t('eyebrow', 'Insights & Narratives')}</p>
            </div>
            <h1 className="font-serif text-6xl md:text-9xl text-stone-900 leading-[0.9] -ml-1">
              {t('title', 'The Journal')}
            </h1>
          </div>
          
          {/* Categories */}
          <div className={`flex flex-wrap gap-8 mt-12 md:mt-0 transition-all duration-1000 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '200ms' }}>
            {(['ALL', 'MARKET', 'DESIGN', 'LIFESTYLE', 'AGENCY'] as NewsCategory[]).map((cat) => (
              <button 
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-xs uppercase tracking-widest transition-colors duration-500 relative py-2 group font-sans ${
                  activeCategory === cat ? 'text-stone-900' : 'text-stone-400 hover:text-stone-600'
                }`}
              >
                {categoryLabels[cat]}
                <span className={`absolute bottom-0 left-0 w-full h-px bg-stone-900 transition-transform duration-500 origin-right ${
                  activeCategory === cat ? 'scale-x-100 origin-left' : 'scale-x-0 group-hover:scale-x-50 group-hover:origin-left'
                }`} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* --- ACT 1: THE LEAD (Featured) --- */}
      {featuredArticle && activeCategory === 'ALL' && (
        <section className={`container mx-auto px-6 md:px-12 mb-32 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{ transitionDelay: '300ms' }}>
          <Link 
            href={getArticleHref(featuredArticle)}
            className="group relative w-full cursor-pointer block"
            onClick={() => handleSelect(featuredArticle.id)}
          >
            <div className="w-full aspect-[4/3] md:aspect-[21/9] overflow-hidden relative">
              {featuredArticle.image && (
                <Image 
                  src={featuredArticle.image}
                  alt={featuredArticle.title}
                  fill
                  className="object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105"
                />
              )}
              <div className="absolute inset-0 bg-stone-900/10 group-hover:bg-stone-900/0 transition-colors duration-1000" />
            </div>
            
            {/* Content Overlay - Floating Card Style for Luxury feel */}
            <div className="absolute bottom-0 left-0 md:left-12 transform md:translate-y-1/2 w-full md:w-auto bg-stone-50 p-8 md:p-12 border-t md:border border-stone-200 md:shadow-2xl md:max-w-2xl">
              <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-stone-400 mb-6 font-sans">
                <span className="text-stone-900 font-medium">{featuredArticle.category}</span>
                <span className="w-px h-3 bg-stone-300" />
                <span>{formatDate(featuredArticle.date, locale)}</span>
                <span className="w-px h-3 bg-stone-300" />
                <span>{featuredArticle.readTime}</span>
              </div>
              <h2 className="font-serif text-3xl md:text-5xl text-stone-900 italic mb-6 leading-tight">
                {featuredArticle.title}
              </h2>
              <p className="font-sans font-light text-stone-600 text-sm md:text-base leading-relaxed mb-8 line-clamp-3">
                {featuredArticle.excerpt}
              </p>
              <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-stone-900 group-hover:gap-6 transition-all duration-500 font-sans">
                <span className="border-b border-stone-900 pb-1">{t('readFullStory', 'Read Full Story')}</span>
                <ArrowRight size={14} />
              </div>
            </div>
          </Link>
          {/* Spacer for the overlapping card */}
          <div className="hidden md:block h-24" />
        </section>
      )}

      {/* --- ACT 2: THE GRID (The Edit) --- */}
      <section className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-24">
          
          {filteredArticles.map((article, idx) => (
            <Link
              key={article.id}
              href={getArticleHref(article)}
              onClick={() => handleSelect(article.id)}
              className={`group cursor-pointer transition-all duration-1000 block ${
                article.category?.toUpperCase() === 'MARKET' ? 'col-span-1 md:col-span-2' : ''
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${400 + (idx * 100)}ms` }}
            >
              {/* MARKET LAYOUT: Horizontal / Technical */}
              {article.category?.toUpperCase() === 'MARKET' ? (
                <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-center bg-white border border-stone-200 p-8 md:p-12 hover:border-stone-400 transition-colors duration-500">
                  <div className="w-full md:w-5/12 overflow-hidden aspect-[4/3] md:aspect-square relative">
                    <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-widest text-stone-900 border border-stone-100 font-sans">
                      {t('marketReport', 'Market Report')}
                    </div>
                    {article.image && (
                      <Image 
                        src={article.image}
                        alt={article.title}
                        fill
                        className="object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-[1500ms]"
                      />
                    )}
                  </div>
                  <div className="w-full md:w-7/12 space-y-8">
                    <div className="flex justify-between items-start border-b border-stone-100 pb-6">
                      <div className="space-y-2">
                        <span className="text-[10px] font-sans text-stone-400 uppercase tracking-widest block">{t('analysis', 'Analysis')}</span>
                        <h3 className="font-serif text-3xl md:text-4xl text-stone-900 group-hover:text-stone-600 transition-colors duration-300">
                          {article.title}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-full border border-stone-200 flex items-center justify-center group-hover:bg-stone-900 group-hover:text-white group-hover:border-stone-900 transition-all duration-500 shrink-0">
                        <ArrowUpRight size={20} />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-8">
                      <p className="col-span-2 font-sans text-stone-500 font-light text-sm leading-relaxed border-l border-stone-200 pl-6">
                        {article.excerpt}
                      </p>
                    </div>

                    <div className="flex items-center gap-6 pt-2">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-400 font-sans">
                        <span className="w-2 h-2 rounded-full bg-stone-300 group-hover:bg-green-500 transition-colors duration-500" />
                        {article.author}
                      </div>
                      <span className="text-stone-300">|</span>
                      <span className="text-[10px] uppercase tracking-widest text-stone-400 font-sans">{formatDate(article.date, locale)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                /* STANDARD LAYOUT: Vertical / Editorial */
                <div className="flex flex-col gap-6">
                  <div className="relative w-full aspect-[16/10] overflow-hidden bg-stone-200">
                    {article.image && (
                      <Image 
                        src={article.image}
                        alt={article.title}
                        fill
                        className="object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105"
                      />
                    )}
                    {/* Hover overlay with Read Time */}
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-500" />
                    <div className="absolute top-6 right-6 bg-white/90 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-widest text-stone-900 translate-y-[-200%] group-hover:translate-y-0 transition-transform duration-500 font-sans" style={{ transitionDelay: '100ms' }}>
                      {article.readTime}
                    </div>
                  </div>
                  
                  <div className="space-y-4 pr-4 md:pr-12">
                    <div className="flex items-baseline justify-between border-b border-stone-200 pb-4">
                      <span className="text-xs text-stone-500 uppercase tracking-widest group-hover:text-stone-900 transition-colors font-sans">{article.category}</span>
                      <span className="text-xs text-stone-400 font-serif italic">{formatDate(article.date, locale)}</span>
                    </div>
                    <h3 className="font-serif text-3xl text-stone-900 leading-tight group-hover:italic transition-all duration-300">
                      {article.title}
                    </h3>
                    <p className="font-sans text-sm text-stone-500 font-light leading-relaxed line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="pt-2 flex items-center gap-2 text-xs uppercase tracking-widest text-stone-900 opacity-60 group-hover:opacity-100 transition-opacity font-sans">
                      <span className="w-0 group-hover:w-4 h-px bg-stone-900 transition-all duration-300" />
                      {t('read', 'Read')}
                    </div>
                  </div>
                </div>
              )}
            </Link>
          ))}
        </div>

        {/* Pagination / Load More */}
        <div className="mt-40 flex flex-col items-center justify-center space-y-6">
          <div className="w-px h-16 bg-stone-300" />
          <p className="font-serif text-stone-400 italic">{t('endOfCollection', 'End of collection')}</p>
          <Link 
            href={`/${locale}/news/archive`}
            className="group relative px-8 py-4 overflow-hidden border border-stone-200 hover:border-stone-900 transition-colors duration-500"
          >
            <span className="relative z-10 text-xs uppercase tracking-widest text-stone-900 group-hover:text-white transition-colors duration-500 font-sans">{t('viewArchive', 'View Archive')}</span>
            <div className="absolute inset-0 bg-stone-900 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-concept" />
          </Link>
        </div>
      </section>
    </div>
  );
}
